import React from 'react';
import DialpadOutlinedIcon from '@mui/icons-material/DialpadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { StoreUser } from '../../services/authService';
import { formatPhoneNumber } from '../../utils/formatUtils';
import AdminSurface from '../admin/AdminSurface';
import { adminColors, adminTypography } from '../../styles/adminDesignTokens';
import {
    getStoreDetailsUserEmail,
    resolveStoreDetailsUserLoginMethod,
    STORE_DETAILS_USER_LOGIN_LABELS,
} from '../../features/storeDetails/users/utils/storeDetailsUserLogin';
import {
    adminButtonSx,
    adminButtonToneSx,
    adminCombineSx,
    adminEmptyStateSx,
    adminTableSx,
} from '../../styles/adminComponentStyles';

interface StoreUsersTableProps {
    users: StoreUser[];
    loading: boolean;
    error: string;
    mutatingUserId?: string | null;
    removingUserId?: string | null;
    ownerUserId?: string | null;
    onRemoveUser?: (user: StoreUser) => void;
    onToggleDailyReconciliationEmail?: (user: StoreUser) => void;
    onToggleVisibility?: (user: StoreUser) => void;
}

const isOwnerUser = (user: StoreUser, ownerUserId?: string | null): boolean => {
    if (ownerUserId && user.user_id === ownerUserId) return true;
    const role = (user.role || '').toLowerCase();
    return role === 'owner' || role === 'store_owner' || role.includes('owner');
};

const preferenceCellSx = {
    width: 132,
    minWidth: 132,
    maxWidth: 132,
} as const;

const StoreUsersTable: React.FC<StoreUsersTableProps> = ({
    users,
    loading,
    error,
    mutatingUserId = null,
    removingUserId = null,
    ownerUserId = null,
    onRemoveUser,
    onToggleDailyReconciliationEmail,
    onToggleVisibility,
}) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <AdminSurface sx={{ p: 2 }}>
                <Typography sx={{ ...adminTypography.body, color: adminColors.danger }}>
                    {error}
                </Typography>
            </AdminSurface>
        );
    }

    if (users.length === 0) {
        return (
            <Box sx={adminEmptyStateSx}>
                <Typography className="AdminEmptyState-title">
                    No users found for this store.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer
            component={AdminSurface}
            sx={adminCombineSx(adminTableSx, { border: 'none', borderRadius: 0, mt: 1.5, overflowX: 'auto' })}
        >
            <Table sx={{
                tableLayout: 'fixed',
                minWidth: 1236,
                width: '100%',
                '& .MuiTableCell-root': {
                    padding: '8px 12px !important',
                },
            }}>
                <TableHead
                    sx={{
                        '& .MuiTableCell-head': {
                            padding: '8px 12px !important',
                            textTransform: 'uppercase !important',
                        },
                    }}
                >
                    <TableRow>
                        <TableCell sx={{ width: 110 }}>Role</TableCell>
                        <TableCell sx={{ width: 220 }}>Name</TableCell>
                        <TableCell sx={{ width: 132 }}>Login Type</TableCell>
                        <TableCell sx={{ width: 250 }}>Email Address</TableCell>
                        <TableCell sx={{ width: 150 }}>Phone</TableCell>
                        <TableCell sx={preferenceCellSx}>Daily Email</TableCell>
                        <TableCell sx={{ width: 132 }}>Visibility</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => {
                        const isOwner = isOwnerUser(user, ownerUserId);
                        const role = (user.role || '').toLowerCase();
                        const isOperator = role === 'operator' || role.includes('operator');
                        const canReceiveDailyEmail = !isOwner && !isOperator;
                        const isMutating = mutatingUserId === user.user_id;
                        const isRemoving = removingUserId === user.user_id;
                        const loginMethod = resolveStoreDetailsUserLoginMethod(user);
                        const loginColor = loginMethod === 'pin' ? '#7C3AED' : adminColors.primary;
                        const userEmail = getStoreDetailsUserEmail(user);

                        return (
                            <TableRow key={user.id}>
                                <TableCell>
                                    <Typography sx={adminTypography.body}>
                                        {user.role ? user.role.toUpperCase() : '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography sx={adminTypography.body}>
                                        {user.display_name || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: loginColor }}>
                                        {loginMethod === 'email' ? <EmailOutlinedIcon fontSize="small" /> : null}
                                        {loginMethod === 'pin' ? <DialpadOutlinedIcon fontSize="small" /> : null}
                                        {loginMethod === 'phone' ? <PhoneOutlinedIcon fontSize="small" /> : null}
                                        <Typography sx={{ ...adminTypography.bodyStrong, color: 'inherit' }}>
                                            {STORE_DETAILS_USER_LOGIN_LABELS[loginMethod]}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography sx={adminCombineSx(adminTypography.body, { overflowWrap: 'anywhere' })}>
                                        {loginMethod === 'pin' ? '—' : userEmail || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography sx={adminTypography.body}>
                                        {loginMethod === 'pin' ? '—' : formatPhoneNumber(user.phone_number) || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={preferenceCellSx}>
                                    {canReceiveDailyEmail ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Switch
                                                size="small"
                                                checked={Boolean(user.receive_daily_reconciliation_email)}
                                                disabled={isMutating || !onToggleDailyReconciliationEmail}
                                                onChange={() => onToggleDailyReconciliationEmail?.(user)}
                                                inputProps={{ 'aria-label': `Toggle daily reconciliation email for ${user.display_name || 'user'}` }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: adminColors.primary,
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: adminColors.primary,
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    ) : (
                                        <Typography sx={{ ...adminTypography.helper, color: adminColors.subtleText }}>
                                            —
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ width: 132 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 116, minWidth: 116 }}>
                                        <Switch
                                            size="small"
                                            checked={!user.is_hidden}
                                            disabled={isMutating || !onToggleVisibility}
                                            onChange={() => onToggleVisibility?.(user)}
                                            inputProps={{ 'aria-label': `Toggle ${user.display_name || 'user'} visibility` }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: adminColors.primary,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: adminColors.primary,
                                                },
                                            }}
                                        />
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">
                                    {!isOwner ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={isMutating || isRemoving || !onRemoveUser}
                                            onClick={() => onRemoveUser?.(user)}
                                            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.danger, {
                                                backgroundColor: adminColors.surface,
                                                color: adminColors.danger,
                                                minHeight: 28,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(180, 35, 24, 0.08)',
                                                },
                                            })}
                                        >
                                            {isRemoving ? 'Removing...' : 'Remove'}
                                        </Button>
                                    ) : (
                                        <Typography sx={{ ...adminTypography.helper, color: adminColors.subtleText }}>
                                            —
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default StoreUsersTable;
